// Canonical fixture identity — ONE source of truth shared by Dashboard,
// /app/live and MatchDetail current-state lookup.
//
// Upstream surfaces expose the same fixture under several field names
// (fixture_id, match_id, provider ids, id, …) and as either a number OR a
// string. A strict `===` on a single field therefore misses legitimately
// matching rows (e.g. the signal carries fixture_id while the live-now feed
// keys the same match under match_id, or one side is numeric). We normalize
// EVERY candidate id to a trimmed string and match on set intersection.
// This is deterministic identity matching — never fuzzy team-name matching.

const ID_FIELDS = [
  "fixture_id", "fixtureId", "fixture",
  "match_id", "matchId", "match",
  "provider_fixture_id", "providerFixtureId", "provider_match_id", "providerMatchId",
  "event_id", "eventId", "game_id", "gameId",
  "id",
];

export function fixtureKeys(obj: any): string[] {
  if (obj == null) return [];
  if (typeof obj === "string" || typeof obj === "number") {
    const s = String(obj).trim();
    return s ? [s] : [];
  }
  if (typeof obj !== "object") return [];
  const out = new Set<string>();
  for (const f of ID_FIELDS) {
    const v = obj[f];
    if ((typeof v === "string" || typeof v === "number") && String(v).trim()) out.add(String(v).trim());
  }
  // nested provider shape: { fixture: { id, … } }
  if (obj.fixture && typeof obj.fixture === "object") for (const k of fixtureKeys(obj.fixture)) out.add(k);
  return [...out];
}

export function primaryFixtureKey(obj: any): string | null {
  const k = fixtureKeys(obj);
  return k.length ? k[0] : null;
}

// True when two objects/ids share at least one canonical identity key.
export function fixtureMatches(a: any, b: any): boolean {
  const ka = new Set(fixtureKeys(a));
  if (!ka.size) return false;
  return fixtureKeys(b).some((k) => ka.has(k));
}
